import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod"
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useToast } from "../hooks/useToast";
import { UserPlus, Edit, Trash2, User } from "lucide-react";
import { useAuth } from "../components/features/AuthContext";
import { apiRequest } from "../components/lib/queryClient";
import { ApiResponse } from "../components/lib/utils";
import { decryptData, generateFernetKey } from '../utilities/cryptoUtils'
import Loading from '../components/ui/Loading'

const userSchema = z.object({
  id: z.string().optional(),
  email: z.string().email("Invalid email"),
  password: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 6, {
      message: "Password must be at least 6 characters",
    }),
  role: z.enum(["admin", "user"]),
  tradingModes: z.array(z.enum(["crypto", "stocks"])).optional(),
  copyTrading: z.boolean().optional(),
});

export const changeEncryptionKey = async (data: any) => {
  return await apiRequest("POST", "api/set-encryption-key", data)
}

export type UserFormData = z.infer<typeof userSchema>;

export default function UserManagement() {
  const { user, authorized, loading } = useAuth();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [encryptionKey, setEncryptionKey] = useState<any>(null)
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const [localCopyTrading, setLocalCopyTrading] = useState<boolean>(false);


  const { toast } = useToast();
  const queryClient = useQueryClient();

  const copyTradingMutation = useMutation({
    mutationFn: (data: { user_id: string; copyTrading: boolean }) =>
      apiRequest("PUT", "api/users/copy-trading", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({
        title: "Success",
        description: `Copy trading ${copyTradingEnabled ? "enabled" : "disabled"} successfully`,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update copy trading setting",
        variant: "destructive",
      });
    },
  });

  const handleUserSelect = (userId: string) => {
  setSelectedUserId(userId);

  const selected = users?.find(u => u.id === userId);
  setLocalCopyTrading(selected?.copyTrading ?? false);
};

  const handleCopyTradingSubmit = () => {
  if (!selectedUserId) {
    toast({
      title: "Error",
      description: "Please select a user",
      variant: "destructive",
    });
    return;
  }

  copyTradingMutation.mutate({
    user_id: selectedUserId,
    copyTrading: localCopyTrading,
  });
};


  const { data: users, isLoading } = useQuery<UserFormData[]>({
    queryKey: ["/api/users"],
    queryFn: async () => {
      const res = await apiRequest<ApiResponse<UserFormData[]>>("GET", "api/users");
      return res.data;
    },
  });



  function generateAndSetKey() {
    let key = generateFernetKey()
    setEncryptionKey(key);
  }


  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: "",
      password: "",
      role: "user",
      tradingModes: [], // <-- Add this
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: UserFormData) =>
      apiRequest("POST", "api/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      form.reset();
      setDialogOpen(false);
      toast({
        title: "Success",
        description: "User created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create user",
        variant: "destructive",
      });
    },
  });

  const encryptionMutation = useMutation({
    mutationFn: async (key: string) => {
      const encryptedUser = localStorage.getItem("user");
      const decryptedUser = encryptedUser ? decryptData(encryptedUser) : null;

      const encryption_payload = {
        key,
        user_id: decryptedUser?.userId || "NA",
      };

      return await changeEncryptionKey(encryption_payload);
    },
    onSuccess: (res) => {
      toast({
        title: "Success",
        description: "Encryption key successfully updated",
      });
      setEncryptionKey("");
    },
    onError: (error: any) => {
      console.error("Error changing encryption key:", error);
      toast({
        title: "Error",
        description: error?.response?.message || error.message || "Failed to update encryption key",
        variant: "destructive",
      });
    },
  });


  const updateMutation = useMutation({
    mutationFn: (data: UserFormData) =>
      apiRequest("PUT", "api/users", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setEditingUser(null);
      setDialogOpen(false);
      form.reset();
      toast({
        title: "Success",
        description: "User updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `api/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({
        title: "Success",
        description: "User deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete user",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: UserFormData) => {
    if (editingUser) {
      const updateData = { ...data, id: editingUser.id };

      if (!data.password) {
        delete updateData.password;
      }

      updateMutation.mutate(updateData);
    } else {
      createMutation.mutate(data);
    }
  };

  const handleKeySubmit = () => {
    if (!encryptionKey) {
      toast({
        title: "Error",
        description: "Encryption key cannot be empty",
        variant: "destructive",
      });
      return;
    }

    encryptionMutation.mutate(encryptionKey);
  };

  const handleEdit = (userToEdit: any) => {
    setEditingUser(userToEdit);
    form.reset({
      email: userToEdit.email,
      password: "",
      role: userToEdit.role,
      tradingModes: [],
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingUser(null);
    form.reset();
  };

  if (user?.role !== "admin") {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-gray-500">Admin access required for user management.</p>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return <Loading fullScreen />;
  }

  const selectedUser = users?.find(u => u.id === selectedUserId);
  const copyTradingEnabled = selectedUser?.copyTrading ?? false;

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-6 space-y-4">

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingUser(null)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="min-h-560">
                <DialogHeader>
                  <DialogTitle>
                    {editingUser ? "Edit User" : "Create New User"}
                  </DialogTitle>
                </DialogHeader>
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 relative">
                    <div className="mb-24">
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="Email" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              Password {editingUser && "(leave blank to keep current)"}
                            </FormLabel>
                            <FormControl>
                              <Input type="text" placeholder="Password" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="tradingModes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Trading Modes</FormLabel>
                            <div className="flex gap-4">
                              {(["crypto", "stocks"] as const).map((mode) => (
                                <label key={mode} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    value={mode}
                                    checked={field.value?.includes(mode)}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      if (checked) {
                                        field.onChange([...(field.value ?? []), mode]);
                                      } else {
                                        field.onChange(field.value ?? [].filter((m: string) => m !== mode));
                                      }
                                    }}
                                    className="accent-primary"
                                  />
                                  <span>{mode}</span>
                                </label>
                              ))}
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="role"
                        render={({ field }) => (
                          <FormItem className="relative">
                            <FormLabel>Role</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select role" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="user">User</SelectItem>
                                <SelectItem value="admin">Admin</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="flex justify-end space-x-2 mt-10">
                      <Button type="button" variant="outline" onClick={handleDialogClose}>
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={createMutation.isPending || updateMutation.isPending}
                      >
                        {editingUser ? "Update" : "Create"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>

            <div className="flex items-center justify-between mb-6 space-y-4">
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                {/* Your existing DialogTrigger code remains the same */}
              </Dialog>
            </div>

          </div>


          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">User</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users && users.length > 0 ? (
                  users.map((userItem: any) => (
                    <tr key={userItem.id}>
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                            <User className="w-4 h-4 text-black" />
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">{userItem.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={userItem.role === "admin" ? "default" : "secondary"}>
                          {userItem.role}
                        </Badge>
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(userItem)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMutation.mutate(userItem.id)}
                            disabled={deleteMutation.isPending || userItem.id === user?.userId}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="w-full max-w-2xl">
            <h1 className="font-bold text-sm text-foreground mb-2">Set encryption key:</h1>
            <div className="flex gap-2">
              <Input
                type="text"
                placeholder="Set encryption key"
                value={encryptionKey}
                onChange={(e) => {
                  setEncryptionKey(e.target.value);
                }}
                className="flex-grow"
              />
              <Button
                variant="outline"
                onClick={handleKeySubmit}
                disabled={encryptionMutation.isPending}
              >
                {encryptionMutation.isPending ? "Submitting..." : "Submit"}
              </Button>
              <Button
                variant="secondary"
                onClick={generateAndSetKey}
              >
                Generate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-sm">
        <CardContent className="p-6">
          <div className="w-full max-w-2xl space-y-6">

            {/* Header */}
            <div className="space-y-1">
              <h2 className="text-lg font-semibold tracking-tight">
                Copy Trading Settings
              </h2>
              <p className="text-sm text-muted-foreground">
                Manage automatic trade mirroring for selected users.
              </p>
            </div>

            {/* User Selector */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Select User
              </label>
              <Select onValueChange={handleUserSelect} value={selectedUserId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose a user to configure" />
                </SelectTrigger>
                <SelectContent>
                  {users?.map((u: any) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Divider */}
            <div className="h-px bg-border" />

            {/* Copy Trading Toggle Section */}
            <div className="flex items-center justify-between rounded-xl border bg-muted/30 px-5 py-4">
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  Enable Copy Trading
                </p>
                <p className="text-xs text-muted-foreground">
                  Automatically mirror trades from master account
                </p>
              </div>

              <button
  type="button"
  role="switch"
  aria-checked={localCopyTrading}
  onClick={() => {
    if (!selectedUserId) return;
    setLocalCopyTrading(prev => !prev);
  }}
  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200
    ${localCopyTrading ? "bg-green-500" : "bg-gray-300"}`}
>
  <span
    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-200
      ${localCopyTrading ? "translate-x-6" : "translate-x-1"}`}
  />
</button>
            </div>

            {/* Status Display */}
            {selectedUserId && (
              <div
                className={`flex items-center justify-between rounded-lg px-4 py-3 text-sm font-medium
            ${copyTradingEnabled
                    ? "bg-green-500/10 text-green-600"
                    : "bg-red-500/10 text-red-600"
                  }`}
              >
                <span>
                  {copyTradingEnabled
                    ? "Copy Trading is Enabled"
                    : "Copy Trading is Disabled"}
                </span>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-center bg-gray-100 hover:bg-gray/80 rounded-lg border">
              <Button
                onClick={handleCopyTradingSubmit}
                disabled={copyTradingMutation.isPending || !selectedUserId}
                className="px-6"
              >
                {copyTradingMutation.isPending ? "Saving Changes..." : "Save Changes"}
              </Button>
            </div>

          </div>
        </CardContent>
      </Card>
    </div>
  );
}